import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { DeliverySlotBadge } from './DeliverySlotBadge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Minus, Plus, Trash2, PackagePlus, MapPin, FileText, CreditCard, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Dispatcher-only dialog for editing an order's line items AND delivery details.
 * - Items: +/- quantity, remove, add from inventory.
 * - Details: delivery address, payment method, notes.
 * - Two PATCH calls (items + details) with diff-only writes — both run in parallel on Save.
 * - Disallowed when status is not pending/confirmed/preparing (caller gates it).
 */
export function EditOrderItemsDialog({ open, onOpenChange, order, products, onUpdated, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Phase 5.9.25 — status timeline + SLA flags
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // Phase 6.0.2 — timeline is collapsed by default; previously the section
  // would grow unbounded and prevent scrolling on mobile (the dialog had no
  // overall overflow). Operators usually just need the headline status; they
  // can expand the full audit trail when needed.
  const [timelineOpen, setTimelineOpen] = useState(false);

  // Seed state when dialog opens
  useEffect(() => {
    if (open && order) {
      setItems((order.items || []).map(i => ({
        product_id: i.product_id,
        product_name: i.product_name,
        quantity: i.quantity,
        price: i.price,
        // Carry the variant size through edit state so the size pill stays
        // visible after qty changes (and survives a Save round-trip).
        ...(i.size ? { size: i.size, eighths: i.eighths } : {}),
      })));
      setDeliveryAddress(order.delivery_address || '');
      setPaymentMethod(order.payment_method || 'cash');
      setNotes(order.notes || '');
    }
  }, [open, order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 5.9.25 — load the order status timeline when dialog opens.
  // Powers the "STATUS TIMELINE" section with elapsed time per transition
  // + SLA flags for orders that took too long to move forward.
  useEffect(() => {
    if (!open || !order?.id) return;
    setTimelineLoading(true);
    axios.get(`${API_URL}/api/orders/${order.id}/timeline`, { withCredentials: true })
      .then(({ data }) => setTimeline(data.events || []))
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false));
  }, [open, order?.id]);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );

  const productsById = useMemo(
    () => Object.fromEntries((products || []).map(p => [p.id, p])),
    [products]
  );

  const availableToAdd = useMemo(() => {
    const inOrder = new Set(items.map(i => i.product_id));
    return (products || []).filter(p => !inOrder.has(p.id) && p.stock > 0);
  }, [products, items]);

  const changeQty = (productId, delta) => {
    setItems(prev => prev.map(it => {
      if (it.product_id !== productId) return it;
      const nextQty = it.quantity + delta;
      if (nextQty <= 0) return it; // handled by remove button
      // Respect stock: additional copies must be available
      const stock = productsById[productId]?.stock ?? 0;
      const currentInOrder = order?.items?.find(oi => oi.product_id === productId)?.quantity ?? 0;
      const maxAddableBeyondOriginal = stock; // stock already reflects what's held by this order
      // Allow any quantity up to (currentInOrder + stock) since stock returned is stock not held by this order
      const cap = currentInOrder + maxAddableBeyondOriginal;
      if (delta > 0 && nextQty > cap) {
        toast.error(`Only ${cap} in stock for ${it.product_name}`);
        return it;
      }
      return { ...it, quantity: nextQty };
    }));
  };

  const removeItem = (productId) => {
    if (items.length <= 1) {
      toast.error('Order must have at least one item');
      return;
    }
    setItems(prev => prev.filter(i => i.product_id !== productId));
  };

  const addProduct = (productId) => {
    const p = productsById[productId];
    if (!p) return;
    setItems(prev => [
      ...prev,
      { product_id: p.id, product_name: p.name, quantity: 1, price: p.price }
    ]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Diff items vs original
      const itemsChanged = (() => {
        const orig = order.items || [];
        if (orig.length !== items.length) return true;
        const origMap = Object.fromEntries(orig.map(i => [i.product_id, i.quantity]));
        return items.some(i => origMap[i.product_id] !== i.quantity);
      })();

      // Diff details vs original
      const detailsPatch = {};
      if ((order.delivery_address || '') !== deliveryAddress) detailsPatch.delivery_address = deliveryAddress;
      if ((order.payment_method || 'cash') !== paymentMethod) detailsPatch.payment_method = paymentMethod;
      if ((order.notes || '') !== notes) detailsPatch.notes = notes;

      const calls = [];
      if (itemsChanged) {
        calls.push(axios.patch(
          `${API_URL}/api/orders/${order.id}/items`,
          { items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })) },
          { withCredentials: true }
        ));
      }
      if (Object.keys(detailsPatch).length > 0) {
        calls.push(axios.patch(
          `${API_URL}/api/orders/${order.id}/details`,
          detailsPatch,
          { withCredentials: true }
        ));
      }
      if (calls.length === 0) {
        toast.info('No changes to save');
        onOpenChange(false);
        return;
      }
      await Promise.all(calls);
      toast.success('Order updated');
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#050003] border border-[#39FF14] rounded-none text-[#A3C7A8] font-mono max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#39FF14] font-mono">
            {'>'} {readOnly ? 'VIEW' : 'EDIT'} ORDER #{order.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
          <p className="text-[#4B6B52] text-xs mt-1">
            {'// Customer: '}
            {/* Phase 5.9.29 — clickable customer name jumps to User Management */}
            {order.customer_id ? (
              <Link
                to={`/dispatch/admin/users?openUser=${order.customer_id}`}
                className="text-[#A3C7A8] hover:text-[#39FF14] hover:underline"
                data-testid="order-customer-link"
              >
                {order.customer_name}
              </Link>
            ) : (
              <span className="text-[#A3C7A8]">{order.customer_name}</span>
            )}
            {' · Status: '}{order.status?.toUpperCase()}
            {order.driver_id && order.driver_name && (
              <>
                {' · Driver: '}
                <Link
                  to={`/dispatch/admin/users?openUser=${order.driver_id}`}
                  className="text-[#A3C7A8] hover:text-[#39FF14] hover:underline"
                  data-testid="order-driver-link"
                >
                  {order.driver_name}
                </Link>
              </>
            )}
            {order.region && <> · Region: <span className="text-[#A3C7A8]">{order.region.toUpperCase()}</span></>}
            {order.delivery_slot && <> · <DeliverySlotBadge order={order} testId="order-dialog-slot" /></>}
            {readOnly && <span className="text-[#FF9F0A] ml-2">· READ-ONLY (post-fulfillment)</span>}
          </p>
        </DialogHeader>

        {/* Order details — address, payment, notes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-[#2A0F3D] pb-3">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-[#6B8E73] text-xs uppercase flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Delivery address
            </Label>
            <Input
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              disabled={readOnly}
              className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] focus:border-[#39FF14] rounded-none font-mono disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="edit-address"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[#6B8E73] text-xs uppercase flex items-center gap-1">
              <CreditCard className="w-3 h-3" /> Payment
            </Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod} disabled={readOnly}>
              <SelectTrigger
                className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] rounded-none font-mono"
                data-testid="edit-payment"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                <SelectItem value="cash" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">Cash</SelectItem>
                <SelectItem value="cashapp" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">Cash App</SelectItem>
                <SelectItem value="applepay" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">Apple Pay</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-1">
            <Label className="text-[#6B8E73] text-xs uppercase flex items-center gap-1">
              <FileText className="w-3 h-3" /> Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
              rows={2}
              placeholder="Add or edit delivery notes..."
              className="bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] focus:border-[#39FF14] rounded-none font-mono resize-none disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="edit-notes"
            />
          </div>
        </div>

        <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
          {items.map(item => (
            <div
              key={item.product_id}
              className="flex items-center gap-3 p-3 border border-[#2A0F3D] bg-[#0C0612]"
              data-testid={`edit-item-${item.product_id}`}
            >
              <div className="flex-1 min-w-0">
                {/* Feb 2026 fix — put the size chip in a flex row NEXT to the
                    name (not inline inside the truncated <p>). Previously the
                    parent's `truncate` clipped the chip mid-character on
                    narrow screens (e.g. `28g` → `2...`, `[7g` → `[`). */}
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-[#39FF14] font-mono text-sm truncate min-w-0" data-testid={`edit-item-name-${item.product_id}`}>
                    {item.product_name}
                  </p>
                  {item.size && (
                    <span
                      className="flex-shrink-0 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-[#FFD60A] text-[#FFD60A] rounded-none whitespace-nowrap"
                      data-testid={`edit-item-size-${item.product_id}`}
                    >
                      {item.size}
                    </span>
                  )}
                </div>
                <p className="text-[#4B6B52] font-mono text-xs">
                  ${Number(item.price ?? 0).toFixed(2)} ea · subtotal ${(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
              <div className="flex items-center border border-[#39FF14]">
                {readOnly ? (
                  // Phase 6.0.2 — Post-fulfillment orders show just the qty,
                  // not editable controls. Previously the +/- and trash were
                  // visible-but-disabled which confused operators viewing
                  // delivered orders (looked like they should be clickable).
                  <span
                    className="px-4 py-2 text-[#39FF14] font-mono text-sm min-w-[3ch] text-center"
                    data-testid={`view-qty-${item.product_id}`}
                  >
                    × {item.quantity}
                  </span>
                ) : (
                  <>
                    <button
                      onClick={() => changeQty(item.product_id, -1)}
                      disabled={item.quantity <= 1}
                      className="p-2 text-[#39FF14] hover:bg-[#2A0F3D] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`edit-dec-${item.product_id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 text-[#39FF14] font-mono text-sm min-w-[2ch] text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => changeQty(item.product_id, +1)}
                      className="p-2 text-[#39FF14] hover:bg-[#2A0F3D] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`edit-inc-${item.product_id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
              {!readOnly && (
                <button
                  onClick={() => removeItem(item.product_id)}
                  className="p-2 text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] border border-[#FF3B30] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`edit-remove-${item.product_id}`}
                  title="Remove from order"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>

        {!readOnly && availableToAdd.length > 0 && (
          <div className="border-t border-[#2A0F3D] pt-3 flex items-center gap-2">
            <PackagePlus className="w-4 h-4 text-[#39FF14]" />
            <span className="text-[#6B8E73] font-mono text-xs uppercase">Add item:</span>
            <Select onValueChange={addProduct}>
              <SelectTrigger
                className="h-8 bg-[#050003] border-[#39FF14] text-[#39FF14] font-mono text-xs rounded-none flex-1"
                data-testid="edit-add-product"
              >
                <SelectValue placeholder="Pick a product…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none max-h-64">
                {availableToAdd.map(p => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]"
                  >
                    <span className="flex items-center gap-2">
                      {p.name}
                      <span className="text-[#6B8E73] text-xs">· ${Number(p.price ?? 0).toFixed(2)}</span>
                      {p.stock <= 5 && (
                        <span className="text-[#FF9F0A] text-[10px]">· low ({p.stock})</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Phase 5.9.25 — STATUS TIMELINE. Each transition with elapsed time
            from the previous and an SLA flag when the gap exceeds the
            target threshold. Powers driver/dispatcher process scoring. */}
        {timeline.length > 0 && (() => {
          // SLA targets in minutes — gap from PREVIOUS event to THIS event.
          const SLA_MINUTES = {
            'order.status_confirmed':         30,  // placed → confirmed
            'order.status_preparing':         10,  // confirmed → preparing
            'order.status_out_for_delivery':  30,  // preparing → out_for_delivery
            'order.status_delivered':         45,  // out_for_delivery → delivered
          };
          const EVENT_LABEL = {
            'order.placed':                 'PLACED',
            'order.status_pending':         'PENDING',
            'order.status_confirmed':       'CONFIRMED',
            'order.status_preparing':       'PREPARING',
            'order.status_out_for_delivery':'OUT FOR DELIVERY',
            'order.status_delivered':       'DELIVERED',
            'order.status_cancelled':       'CANCELLED',
          };
          const EVENT_COLOR = {
            'order.placed':                 '#7FD8FF',
            'order.status_confirmed':       '#39FF14',
            'order.status_preparing':       '#FF9F0A',
            'order.status_out_for_delivery':'#FF9F0A',
            'order.status_delivered':       '#39FF14',
            'order.status_cancelled':       '#FF3B30',
          };
          const fmtElapsed = (ms) => {
            if (!ms || ms < 0) return '';
            const s = Math.round(ms / 1000);
            if (s < 60) return `${s}s`;
            const m = Math.round(s / 60);
            if (m < 60) return `${m}m`;
            const h = Math.floor(m / 60);
            const mm = m % 60;
            if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
            const d = Math.floor(h / 24);
            const hh = h % 24;
            return hh ? `${d}d ${hh}h` : `${d}d`;
          };
          const fmtTs = (iso) => {
            try {
              const d = new Date(iso);
              return d.toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              });
            } catch (_) { return iso; }
          };
          return (
            <div className="border border-[#2A0F3D] p-3 space-y-2" data-testid="order-status-timeline">
              <button
                type="button"
                onClick={() => setTimelineOpen(o => !o)}
                className="w-full flex items-center justify-between hover:bg-[#2A0F3D]/30 -m-1 p-1 rounded-none"
                data-testid="timeline-toggle-btn"
              >
                <h3 className="text-[10px] uppercase tracking-wider text-[#7FD8FF] flex items-center gap-2">
                  {timelineOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {'>'} STATUS TIMELINE
                  <span className="text-[#6B8E73] normal-case">({timeline.length})</span>
                </h3>
                {timelineLoading && <span className="text-[9px] text-[#6B8E73] font-mono">LOADING…</span>}
              </button>
              {timelineOpen && (
              <ol className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1" data-testid="timeline-list">
                {timeline.map((ev, idx) => {
                  const prev = idx > 0 ? timeline[idx - 1] : null;
                  const elapsedMs = prev ? new Date(ev.ts).getTime() - new Date(prev.ts).getTime() : 0;
                  const slaMins = SLA_MINUTES[ev.event_type];
                  const slaBreached = slaMins && elapsedMs > slaMins * 60 * 1000;
                  const color = EVENT_COLOR[ev.event_type] || '#A3C7A8';
                  return (
                    <li
                      key={idx}
                      className="flex items-start gap-2 font-mono text-[11px]"
                      data-testid={`timeline-row-${ev.event_type}`}
                    >
                      <div
                        className="w-2 h-2 rounded-full shrink-0 mt-1.5"
                        style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-bold uppercase tracking-wider" style={{ color }}>
                            {EVENT_LABEL[ev.event_type] || ev.event_type}
                          </span>
                          {ev.actor_role && (
                            <span className="text-[9px] uppercase text-[#6B8E73]">
                              · by {ev.actor_role}
                            </span>
                          )}
                          {slaBreached && (
                            <span
                              className="text-[9px] uppercase text-[#FF3B30] font-bold border border-[#FF3B30] px-1 py-px"
                              data-testid={`timeline-sla-breach-${ev.event_type}`}
                              title={`Target: ${slaMins}m · Actual: ${fmtElapsed(elapsedMs)}`}
                            >
                              ⚠ SLA {slaMins}m
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#A3C7A8]/70 flex items-baseline gap-2 flex-wrap">
                          <span>{fmtTs(ev.ts)}</span>
                          {prev && (
                            <span className={slaBreached ? 'text-[#FF3B30]' : 'text-[#6B8E73]'}>
                              + {fmtElapsed(elapsedMs)} after {EVENT_LABEL[prev.event_type] || prev.event_type.replace('order.', '')}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
              )}
            </div>
          );
        })()}

        {/* Phase 5.9.24 — Price breakdown. Surfaces every line that shifts
            subtotal → total so the operator sees exactly why the displayed
            total differs from items × qty. Reads values from the order doc:
              - discount       : dollar value of points redemption
              - redeem_pts     : how many points the customer used
              - delivery_fee   : forthcoming per-region fee (P1 backlog)
              - tip_amount     : forthcoming driver tip
              - tax_amount     : forthcoming regional tax
            Each row only renders when its value is non-zero so the breakdown
            stays compact for simple orders. */}
        {(() => {
          const subtotal = total; // recomputed from current items
          const discount = Number(order?.discount || 0);
          const redeemPts = Number(order?.redeem_pts || 0);
          const deliveryFee = Number(order?.delivery_fee || 0);
          const tip = Number(order?.tip_amount || 0);
          const tax = Number(order?.tax_amount || 0);
          const finalTotal = subtotal - discount + deliveryFee + tip + tax;
          const hasAnyAdj = discount || deliveryFee || tip || tax;
          return (
            <div className="border-t border-[#39FF14] pt-3 space-y-2 font-mono text-sm" data-testid="order-price-breakdown">
              {/* Subtotal row — always shown so the math is anchored */}
              <div className="flex items-center justify-between">
                <span className="text-[#6B8E73] uppercase">Subtotal</span>
                <span className="text-[#A3C7A8]" data-testid="breakdown-subtotal">${Number(subtotal ?? 0).toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between" data-testid="breakdown-discount-row">
                  <span className="text-[#FF9F0A] uppercase">
                    Points Redeemed
                    {redeemPts > 0 && <span className="text-[#A3C7A8]/70 normal-case"> · {redeemPts} pts</span>}
                  </span>
                  <span className="text-[#FF9F0A]">-${Number(discount ?? 0).toFixed(2)}</span>
                </div>
              )}
              {deliveryFee > 0 && (
                <div className="flex items-center justify-between" data-testid="breakdown-delivery-row">
                  <span className="text-[#6B8E73] uppercase">Delivery Fee</span>
                  <span className="text-[#A3C7A8]">+${Number(deliveryFee ?? 0).toFixed(2)}</span>
                </div>
              )}
              {tax > 0 && (
                <div className="flex items-center justify-between" data-testid="breakdown-tax-row">
                  <span className="text-[#6B8E73] uppercase">Tax</span>
                  <span className="text-[#A3C7A8]">+${Number(tax ?? 0).toFixed(2)}</span>
                </div>
              )}
              {tip > 0 && (
                <div className="flex items-center justify-between" data-testid="breakdown-tip-row">
                  <span className="text-[#6B8E73] uppercase">Driver Tip</span>
                  <span className="text-[#A3C7A8]">+${Number(tip ?? 0).toFixed(2)}</span>
                </div>
              )}
              <div className={`flex items-center justify-between ${hasAnyAdj ? 'border-t border-[#2A0F3D] pt-2' : ''}`}>
                <span className="text-[#6B8E73] uppercase">{readOnly ? 'Total' : 'Updated Total'}</span>
                <span className="text-[#39FF14] text-xl font-bold" data-testid="edit-total">
                  ${(hasAnyAdj ? finalTotal : subtotal).toFixed(2)}
                </span>
              </div>
            </div>
          );
        })()}

        <DialogFooter className="flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          {/* Phase 5.9.21 — VIEW AS DRIVER preview. Same `?preview=1` pattern
              as VIEW AS CUSTOMER: same-tab navigation preserves PWA auth,
              ProtectedRoute lets operators through, DriverDashboard surfaces
              a preview banner + disables actionable buttons. */}
          {order?.id && (
            <Link
              to={`/driver?preview=1&order=${order.id}`}
              className="block text-center text-[#7FD8FF] font-mono text-[10px] uppercase tracking-wider py-2 px-3 border border-[#7FD8FF]/30 hover:border-[#7FD8FF] transition-colors rounded-none sm:mr-auto"
              data-testid="view-as-driver-link"
            >
              VIEW AS DRIVER →
            </Link>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#2A0F3D] text-[#6B8E73] hover:border-[#FF3B30] hover:text-[#FF3B30] rounded-none font-mono"
            data-testid="edit-cancel"
          >
            {readOnly ? 'CLOSE' : 'CANCEL'}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              disabled={saving || items.length === 0}
              className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono font-bold"
              data-testid="edit-save"
            >
              {saving ? 'SAVING…' : 'SAVE CHANGES'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
